import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon, ExportAltIcon } from 'cokitchen-icons';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import './styles.scss';
import MealCard from 'components/base/meal-card';
import LowMealsPrompt from 'components/base/low-meals-prompt';
import { useNavigate } from 'react-router-dom';
import { ALL, MAIN_STORE_ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import ExportProductionReportModal from 'components/modals/export-production-report-modal';
import { MealItemType } from 'models/production-report.model';
import BulkAction from './bulk-action';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import useProductionReportContext from 'contexts/kitchen/production-report.context/context';

const ProductionReport = () => {
  const { loading, sections, activeSection, setActiveSection, selectedMeals, setSelectedMeals } =
    useProductionReportContext();
  const [showExportModal, setShowExportModal] = useState(false);
  const [showBulkAction, setShowBulkAction] = useState(false);
  const [mealLoading, setMealLoading] = useState(false);
  const [mealList, setMealList] = useState<MealItemType[]>([]);
  const [filterMealList, setFilterMealList] = useState<MealItemType[]>([]);
  const [lowMeals, setLowMeals] = useState<MealItemType[]>([]);
  const sectionCarouselRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const cokitchenId = localStorage.getItem(STORAGE_KEYS.COKITCHEN);

  useEffect(() => {
    if (selectedMeals?.length > 0) {
      setShowBulkAction(true);
    } else {
      setShowBulkAction(false);
    }
  }, [selectedMeals]);

  useEffect(() => {
    if (activeSection?.id) {
      fetchMeals();
    }
  }, [activeSection]);

  useEffect(() => {
    setSelectedMeals([]);
  }, []);

  useEffect(() => {
    if (mealList?.length > 0) {
      const lowMeals = mealList.filter((meal) => meal.minimun_stock_level >= meal.quantity);
      setLowMeals(lowMeals);
    }
  }, [mealList]);

  const fetchMeals = async () => {
    try {
      setMealLoading(true);
      const params = {
        cokitchen_id: cokitchenId,
        ...(activeSection.id !== ALL && { section_id: activeSection.id })
      };
      const res = await apiService.get('cokitchen-item', {
        params
      });

      setMealList(res.data.results);
      setFilterMealList(res.data.results);
    } catch {
      toast(<ToastMessage message='Error fetching meals' type='error' />);
    } finally {
      setMealLoading(false);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const filtered = mealList?.filter((meal) =>
      meal.kitchen_item.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterMealList(filtered);
  };

  const updateMeals = (data: Partial<MealItemType>) => {
    setMealList(mealList?.map((meal) => (meal.id === data.id ? { ...meal, ...data } : meal)));
    setFilterMealList(
      filterMealList?.map((meal) => (meal.id === data.id ? { ...meal, ...data } : meal))
    );
  };

  const scrollSectionCarousel = (direction: 'left' | 'right') => {
    if (!sectionCarouselRef.current) return;
    const scrollAmount = 200;

    sectionCarouselRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <Fragment>
      {lowMeals?.length > 0 && <LowMealsPrompt />}
      <div className='px-8 w-full production_report'>
        <div className='w-full flex justify-between items-center flex-wrap gap-4'>
          <div>
            <h6 className='text-[24px] font-semibold'>Production report</h6>
            <p className='text-gray-9 text-sm'>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit.
            </p>
          </div>
          <div className='flex gap-3'>
            <Button
              variant='block'
              color='gray'
              onClick={() => setShowExportModal(true)}
              className='h-[40px] w-[139px] text-sm font-medium gap-1 !text-gray-6 !bg-[#F2F2F2] !border-[#F2F2F2]'>
              <ExportAltIcon />
              Export
            </Button>
            <Button
              variant='block'
              color='primary'
              className='h-[40px] w-[169px] text-sm font-medium !p-0'
              onClick={() => navigate(MAIN_STORE_ROUTE_KEYS.KITCHEN_ADD_MEAL)}>
              <AddIcon className='w-4 h-4' />
              Add Kitchen Item
            </Button>
          </div>
          <div className='mt-4 w-full flex justify-end items-center'>
            <Input
              type='search'
              placeholder='Search kitchen item'
              className='w-[300px]'
              inputClass='h-[36px]'
              onChange={handleSearch}
            />
          </div>
          <div className='mt-3 w-full'>
            {loading ? (
              <span />
            ) : sections?.length === 0 ? (
              <div className='w-full flex justify-center items-center'>
                <p>No Sections Found</p>
              </div>
            ) : (
              <div className='flex items-center gap-2 w-full'>
                <button
                  type='button'
                  aria-label='Scroll sections left'
                  className='w-8 h-8 rounded-full border border-[#EDEDEE] text-[#5E6164] flex items-center justify-center'
                  onClick={() => scrollSectionCarousel('left')}>
                  &#8249;
                </button>
                <div
                  ref={sectionCarouselRef}
                  className='flex gap-3 flex-1 overflow-x-scroll flex-nowrap no-scroll-bar'>
                  {sections?.map((section) => {
                    return (
                      <div
                        key={section.id}
                        className={`${
                          activeSection?.id === section.id ? 'bg-[#FEF0F2]' : 'bg-[#F4F5F5]'
                        } rounded-lg px-2 py-[6px] w-fit cursor-pointer`}
                        onClick={() => setActiveSection(section)}>
                        <p
                          className={`${
                            activeSection?.id === section.id ? 'text-red-5' : 'text-[#96999C]'
                          } text-sm whitespace-nowrap capitalize`}>
                          {section.name}
                        </p>
                      </div>
                    );
                  })}
                </div>
                <button
                  type='button'
                  aria-label='Scroll sections right'
                  className='w-8 h-8 rounded-full border border-[#EDEDEE] text-[#5E6164] flex items-center justify-center'
                  onClick={() => scrollSectionCarousel('right')}>
                  &#8250;
                </button>
              </div>
            )}
          </div>
          <div className='w-full'>
            {mealLoading ? (
              <div className='w-full flex justify-center items-center h-[20vh]'>
                <p>Loading meals</p>
              </div>
            ) : filterMealList?.length === 0 ? (
              <div className='w-full flex justify-center items-center h-[20vh]'>
                <p>No meals found</p>
              </div>
            ) : (
              <div className='grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6'>
                {filterMealList?.map((meal) => {
                  return (
                    <MealCard
                      key={meal.kitchen_item_id}
                      meal={meal}
                      selectedMeals={selectedMeals}
                      setSelectedMeals={setSelectedMeals}
                      refetchMeals={fetchMeals}
                      updateMeals={updateMeals}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      {showExportModal && (
        <ExportProductionReportModal
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
        />
      )}
      {showBulkAction && (
        <BulkAction selectedMeals={selectedMeals} setSelectedMeals={setSelectedMeals} />
      )}
    </Fragment>
  );
};

export default ProductionReport;
